export type FlexIndicatorPresentation = {
	label: string,
	isWide: boolean,
	tooltip: string,
};

export const flexIndicatorPresentation = (
	deadline: string | null | undefined,
	checkInDate: string,
): FlexIndicatorPresentation => {
	if (deadline === 'partial' || (deadline != null && deadline.startsWith('partial:'))) {
		const cancellationCost = deadline.startsWith('partial:') ? deadline.substring('partial:'.length) : '';
		return {
			label: 'FP',
			isWide: true,
			tooltip: cancellationCost.length > 0
				? `Partially refundable — costs ${cancellationCost} to cancel.`
				: 'Partially refundable — Booking.com keeps part of the price after a cancellation.',
		};
	}

	const daysBeforeCheckIn = cancellationDaysBeforeCheckIn(deadline, checkInDate);
	const hasDatedDeadline = daysBeforeCheckIn !== undefined;
	return {
		label: hasDatedDeadline ? `F-${daysBeforeCheckIn}` : 'F',
		isWide: hasDatedDeadline,
		tooltip: cancellationTooltip(deadline, daysBeforeCheckIn),
	};
};

const cancellationDaysBeforeCheckIn = (
	deadline: string | null | undefined,
	checkInDate: string,
): number | undefined => {
	if (deadline == null || deadline.length === 0 || deadline === 'anytime') return undefined;
	const deadlineDate = isoDateAsUtc(deadline.substring(0, 10));
	const arrivalDate = isoDateAsUtc(checkInDate.substring(0, 10));
	if (!deadlineDate || !arrivalDate) return undefined;
	const days = Math.round((arrivalDate.getTime() - deadlineDate.getTime()) / 86_400_000);
	return days >= 0 ? days : undefined;
};

const isoDateAsUtc = (value: string): Date | undefined => {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!match) return undefined;
	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const date = new Date(Date.UTC(year, month - 1, day));
	if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
		return undefined;
	}
	return date;
};

const cancellationTooltip = (
	deadline: string | null | undefined,
	daysBeforeCheckIn: number | undefined,
): string => {
	if (deadline === 'anytime') return 'Free cancellation anytime';
	if (deadline == null || deadline.length === 0 || daysBeforeCheckIn === undefined) return 'Lowest flex available rate';

	const date = isoDateAsUtc(deadline.substring(0, 10));
	if (!date) return 'Lowest flex available rate';
	const publishedTime = deadline.includes('T') ? deadline.substring(11, 16) : undefined;
	const formattedDate = new Intl.DateTimeFormat('en-GB', {
		day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
	}).format(date);
	const formattedDeadline = publishedTime !== undefined ? `${formattedDate} at ${publishedTime}` : formattedDate;
	const dayLabel = daysBeforeCheckIn === 1 ? 'day' : 'days';
	return `Free cancellation until ${formattedDeadline} — ${daysBeforeCheckIn} ${dayLabel} before check-in`;
};
